import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { Eye, EyeOff, LogIn, Receipt } from 'lucide-react';
import { Alert, AlertDescription, Button, DecoratedInput, IconButton } from '@/lib/macondo';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/api/client';

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Invalid email or password.';
    if (err.status === 403) return err.message || 'Access to billing is restricted to Spoonity team accounts.';
    if (err.status === 429) return 'Too many sign-in attempts. Wait a few minutes and try again.';
    if (err.status >= 500) return 'Sign-in is temporarily unavailable. Try again shortly.';
    return err.message;
  }
  return 'Could not reach the server. Check your connection and try again.';
}

export default function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const from = (location.state as { from?: Location } | null)?.from;
    navigate(from && from.pathname !== '/login' ? `${from.pathname}${from.search}` : '/', { replace: true });
  }, [status, location.state, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    setShowPassword(false);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(messageFor(err));
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="bg-background flex min-h-screen w-full items-center justify-center p-400">
      <div className="w-full max-w-[440px]">
        <div className="flex items-center gap-200">
          <Receipt size={24} aria-hidden="true" className="text-typography-primary" />
          <span className="text-headline text-typography-primary">Spoonity Billing</span>
        </div>
        <h1 className="text-h2 text-typography-primary mt-600">Sign in</h1>
        <p className="text-callout text-typography-secondary mt-100">Use your Spoonity account, the same one you use for the dashboard and mcp.spoonity.com.</p>

        {error && (
          <Alert tone="critical" className="mt-600" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form className="mt-600 flex flex-col gap-400" onSubmit={onSubmit} noValidate={false}>
          <DecoratedInput
            id="login-email"
            label="Email"
            type="email"
            required
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <DecoratedInput
            id="login-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            rightDecoration={
              <IconButton
                type="button"
                size="sm"
                variant="ghost-square"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                aria-controls="login-password"
                disabled={submitting}
                onClick={() => setShowPassword((v) => !v)}
                icon={showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              />
            }
          />
          <Button type="submit" className="mt-100 w-full" loading={submitting} disabled={!email || !password}>
            <LogIn aria-hidden="true" />
            Sign in
          </Button>
        </form>
      </div>
    </main>
  );
}
